/**
 * Payroll staf tetap — KERANGKA (inventaris A18; Owner: "payroll gaji staff tetap sebelum go live").
 * Data gaji RAHASIA: layar ini hanya Owner + Finance; tiap baca/ubah tercatat di jejak audit.
 * VONIS-26SEP-PAKET-K B-31..33: otomatis gaji pokok + tunjangan tetap + uang makan × hari hadir (Q38) + potongan alpa
 * & izin per hari (sakit/cuti tidak dipotong). PPh 21, BPJS, lembur, THR = baris manual per jenis. Alur: Finance hitung →
 * Owner finalkan (slip bernomor) → Finance bayar. Gajian tanggal 1 bulan berikutnya; bukan hari kerja → hari kerja sesudahnya.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { formatIDR, formatDate } from "../lib/format";
import Hint from "../components/etams/Hint";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import PemilihCari from "../components/etams/PemilihCari";
import { cariKaryawan, labelKaryawan, ketKaryawan } from "../lib/sumberPilihan";

const kosongMaster = { employee_id: "", jenis_periode: "BULANAN", gaji_pokok_idr: "", uang_makan_harian_idr: "", tunjangan_tetap_idr: "", berlaku_sejak: "" };
const JENIS_MANUAL = [
  ["PPH21", "PPh 21 (potongan)"], ["BPJS_KESEHATAN", "BPJS Kesehatan (potongan)"],
  ["BPJS_KETENAGAKERJAAN", "BPJS Ketenagakerjaan (potongan)"], ["LEMBUR", "Lembur (tambahan)"],
  ["THR", "THR (tambahan)"], ["LAIN", "Lain-lain (minus = potongan)"],
];
// Label untuk staf — kode mentah jenis/keadaan periode tidak tampil (K200 butir 6).
const LABEL_JENIS_PERIODE = { BULANAN: "Bulanan", MINGGUAN: "Mingguan" };
const LABEL_STATUS_PERIODE = { DRAFT: "Draf", FINAL: "Final" };
const kosongPeriode = { region_id: "", jenis_periode: "BULANAN", tanggal_mulai: "", tanggal_selesai: "" };
//: Cermin `URUT_SAH_MASTER` / `URUT_SAH_PERIODE` di backend/services/payroll.py.
const URUT_SAH_MASTER = ["berlaku_sejak", "created_at", "jenis_periode", "gaji_pokok_idr"];
const URUT_SAH_PERIODE = ["tanggal_mulai", "tanggal_selesai", "status", "created_at"];
const KOLOM_CSV_MASTER = [
  { kunci: "karyawan", judul: "Karyawan", ambil: (m) => m.employee?.full_name || "" },
  { kunci: "jenis_periode", judul: "Jenis", ambil: (m) => LABEL_JENIS_PERIODE[m.jenis_periode] || m.jenis_periode },
  { kunci: "gaji_pokok_idr", judul: "Gaji pokok (IDR)" },
  { kunci: "uang_makan_harian_idr", judul: "Uang makan per hari (IDR)" },
  { kunci: "tunjangan_tetap_idr", judul: "Tunjangan tetap (IDR)" },
  { kunci: "berlaku_sejak", judul: "Berlaku sejak" },
];

export default function HRPayroll() {
  const { roleCode } = useAuth();
  const isOwner = roleCode === "OWNER";
  const isFinance = roleCode === "FINANCE";
  const [master, setMaster] = useState([]);
  const [totalMaster, setTotalMaster] = useState(0);
  const [periode, setPeriode] = useState([]);
  const [totalPeriode, setTotalPeriode] = useState(0);
  const [karyawanMaster, setKaryawanMaster] = useState(null);
  const [regions, setRegions] = useState([]);
  const [fm, setFm] = useState(kosongMaster);
  const [fp, setFp] = useState(kosongPeriode);
  const [pilih, setPilih] = useState(null);
  const [slip, setSlip] = useState([]);
  const [sibuk, setSibuk] = useState(false);

  // Pola daftar seragam (28 Sep 2026): DUA daftar di satu layar → awalan alamat `m_` (master gaji) dan `p_`
  // (periode). Keduanya halaman di SERVER — dulu master berhenti diam-diam di 2.000 baris dan periode di 200.
  const dMaster = useDaftar(master, {
    server: { total: totalMaster, urutSah: URUT_SAH_MASTER },
    urutAwal: { kunci: "berlaku_sejak", arah: "desc" },
    awalan: "m_",
    saringan: { jenis: { label: "Jenis", param: "jenis_periode", tampil: (v) => LABEL_JENIS_PERIODE[v] || v } },
  });
  const dPeriode = useDaftar(periode, {
    server: { total: totalPeriode, urutSah: URUT_SAH_PERIODE },
    urutAwal: { kunci: "tanggal_mulai", arah: "desc" },
    awalan: "p_",
    saringan: {
      status: { label: "Status", param: "status", tampil: (v) => LABEL_STATUS_PERIODE[v] || v },
      region: { label: "Wilayah", param: "region_id", tampil: (v) => (regions.find((r) => r.id === v) || {}).name || "wilayah terpilih" },
    },
  });
  const pm = dMaster.paramServer;
  const psm = dMaster.paramSaringan;
  const paramMaster = useCallback((o = {}) => ({ ...psm, ...(pm.cari && { q: pm.cari }), limit: pm.limit,
    offset: pm.offset, sort: pm.sort, arah: pm.arah, ...o }), [psm, pm.cari, pm.limit, pm.offset, pm.sort, pm.arah]);
  const pp = dPeriode.paramServer;
  const psp = dPeriode.paramSaringan;
  const paramPeriode = useCallback((o = {}) => ({ ...psp, ...(pp.cari && { q: pp.cari }), limit: pp.limit, offset: pp.offset, sort: pp.sort,
    arah: pp.arah, ...o }), [psp, pp.cari, pp.limit, pp.offset, pp.sort, pp.arah]);
  const ambilHalamanMaster = useCallback(async (off, n) =>
    (await api.get("/payroll/master", { params: paramMaster({ offset: off, limit: n }) })).data, [paramMaster]);

  const muatMaster = useCallback(async () => {
    try {
      const m = await api.get("/payroll/master", { params: paramMaster() });
      setMaster(m.data.items || []); setTotalMaster(m.data.total ?? (m.data.items || []).length);
    } catch (e) { toast.error(apiErrorMessage(e, "Data payroll gagal dimuat.")); }
  }, [paramMaster]);
  const muatPeriode = useCallback(async () => {
    try {
      const p = await api.get("/payroll/periode", { params: paramPeriode() });
      setPeriode(p.data.items || []); setTotalPeriode(p.data.total ?? (p.data.items || []).length);
    } catch (e) { toast.error(apiErrorMessage(e, "Data payroll gagal dimuat.")); }
  }, [paramPeriode]);
  useEffect(() => { muatMaster(); }, [muatMaster]);
  useEffect(() => { muatPeriode(); }, [muatPeriode]);
  const muat = useCallback(async () => { await Promise.all([muatMaster(), muatPeriode()]); }, [muatMaster, muatPeriode]);
  useEffect(() => {
    api.get("/regions").then((r) => setRegions(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(apiErrorMessage(e, "Data wilayah gagal dimuat.")));
  }, []);

  const muatSlip = useCallback(async (p) => {
    setPilih(p);
    try { const r = await api.get(`/payroll/periode/${p.id}/slip`); setSlip(r.data.items || []); }
    catch (e) { toast.error(apiErrorMessage(e, "Slip gagal dimuat.")); setSlip([]); }
  }, []);

  const jalan = async (fn, ok) => {
    setSibuk(true);
    try { await fn(); toast.success(ok); await muat(); if (pilih) await muatSlip(pilih); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal.")); }
    finally { setSibuk(false); }
  };

  const ubahManual = (s, idx, patch) => setSlip((arr) => arr.map((x) => x.id !== s.id ? x : {
    ...x, _manual: (x._manual ?? (x.baris || []).filter((b) => b.kode === "MANUAL")).map((b, i) => i === idx ? { ...b, ...patch } : b),
  }));
  const tambahManual = (s) => setSlip((arr) => arr.map((x) => x.id !== s.id ? x : {
    ...x, _manual: [...(x._manual ?? (x.baris || []).filter((b) => b.kode === "MANUAL")), { jenis: "PPH21", label: "", nominal: 0 }],
  }));

  const bukaPdf = async (s) => {
    const tab = window.open("", "_blank");   // dibuka DULU — sesudah await diblokir peramban
    try {
      const r = await api.get(`/payroll/slip/${s.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (tab) tab.location.href = url; else window.location.href = url;
    } catch (e) { if (tab) tab.close(); toast.error(apiErrorMessage(e, "Slip PDF gagal dibuka.")); }
  };

  const bayar = (s) => jalan(() => api.post(`/payroll/slip/${s.id}/bayar`, { referensi: (s._ref || "").trim() || null }),
    "Pembayaran tercatat.");

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div>
        <h1 className="text-xl font-semibold">Payroll Staf Tetap</h1>
        <p className="text-sm text-muted-foreground flex items-center gap-1">
          Rahasia — hanya Owner dan Finance; tiap akses tercatat.
          <Hint>Otomatis: gaji pokok, tunjangan tetap, uang makan × hari hadir (izin setengah hari tetap dapat uang makan), potongan alpa &amp; izin (sakit dan cuti tidak dipotong), penyesuaian koreksi absen dari periode yang sudah final (periode itu tidak dibuka ulang); PPh 21, BPJS, lembur, THR diisi manual. Alur: Finance hitung → Owner finalkan → Finance bayar, gajian tanggal 1 bulan berikutnya (atau hari kerja sesudahnya).</Hint>
        </p>
      </div>

      {isOwner && (
        <Card className="p-4 space-y-3" data-testid="payroll-master-form">
          <div className="font-medium">Master gaji (diisi Owner)</div>
          <div className="grid gap-2 sm:grid-cols-3">
            <PemilihCari value={karyawanMaster}
                         onChange={(k) => { setKaryawanMaster(k); setFm((f) => ({ ...f, employee_id: k?.id || "" })); }}
                         cari={(kata) => cariKaryawan(kata)} label={labelKaryawan} keterangan={ketKaryawan}
                         placeholder="Pilih karyawan" placeholderCari="Ketik nama atau kode karyawan…"
                         testId="payroll-master-karyawan" />
            <select className="h-9 rounded-md border px-2 text-sm" value={fm.jenis_periode} onChange={(e) => setFm({ ...fm, jenis_periode: e.target.value })}>
              <option value="BULANAN">Bulanan</option><option value="MINGGUAN">Mingguan</option>
            </select>
            <Input type="date" data-testid="payroll-master-berlaku" value={fm.berlaku_sejak} onChange={(e) => setFm({ ...fm, berlaku_sejak: e.target.value })} title="Berlaku sejak" />
            <Input type="number" min="0" placeholder="Gaji pokok (Rp)" value={fm.gaji_pokok_idr} onChange={(e) => setFm({ ...fm, gaji_pokok_idr: e.target.value })} />
            <Input type="number" min="0" placeholder="Uang makan per hari hadir (Rp)" value={fm.uang_makan_harian_idr} onChange={(e) => setFm({ ...fm, uang_makan_harian_idr: e.target.value })} />
            <Input type="number" min="0" placeholder="Tunjangan tetap (Rp)" value={fm.tunjangan_tetap_idr} onChange={(e) => setFm({ ...fm, tunjangan_tetap_idr: e.target.value })} />
          </div>
          <Button disabled={sibuk || !fm.employee_id || !fm.berlaku_sejak || fm.gaji_pokok_idr === ""}
                  onClick={() => jalan(() => api.post("/payroll/master", { ...fm, gaji_pokok_idr: Number(fm.gaji_pokok_idr),
                    uang_makan_harian_idr: Number(fm.uang_makan_harian_idr || 0), tunjangan_tetap_idr: Number(fm.tunjangan_tetap_idr || 0) })
                    .then(() => { setFm(kosongMaster); setKaryawanMaster(null); }), "Master gaji disimpan.")} data-testid="payroll-master-simpan">Simpan versi gaji</Button>
        </Card>
      )}

      <Card className="p-4">
        <div className="font-medium mb-2">Master gaji tercatat</div>
        <PalangDaftar daftar={dMaster} placeholder="Cari nama / kode karyawan…" testId="payroll-master-palang"
                      ekspor={{ nama: "master_gaji", kolom: KOLOM_CSV_MASTER, ambilHalaman: ambilHalamanMaster }}>
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={dMaster.nilaiSaringan.jenis}
                  onChange={(e) => dMaster.setSaringan("jenis", e.target.value)} aria-label="Saring jenis" data-testid="payroll-master-filter-jenis">
            <option value="">Semua jenis</option><option value="BULANAN">Bulanan</option><option value="MINGGUAN">Mingguan</option>
          </select>
        </PalangDaftar>
        {dMaster.total === 0 ? <div className="text-sm text-muted-foreground">{dMaster.cari.trim() || dMaster.saringanAktif.length ? "Tidak ada yang cocok." : "Belum ada."}</div> : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground"><tr><th className="text-left p-1">Karyawan</th>
              <ThUrut daftar={dMaster} kunci="jenis_periode" className="text-left p-1">Jenis</ThUrut>
              <ThUrut daftar={dMaster} kunci="gaji_pokok_idr" className="text-right p-1" align="right">Pokok</ThUrut>
              <th className="text-right p-1">Makan/hari</th><th className="text-right p-1">Tunjangan</th>
              <ThUrut daftar={dMaster} kunci="berlaku_sejak" className="text-left p-1">Berlaku</ThUrut></tr></thead>
            <tbody>{dMaster.tampil.map((m) => (
              <tr key={m.id} className="border-t"><td className="p-1">{m.employee?.full_name}</td><td className="p-1">{LABEL_JENIS_PERIODE[m.jenis_periode] || m.jenis_periode}</td>
                <td className="p-1 text-right">{formatIDR(m.gaji_pokok_idr)}</td><td className="p-1 text-right">{formatIDR(m.uang_makan_harian_idr)}</td>
                <td className="p-1 text-right">{formatIDR(m.tunjangan_tetap_idr)}</td><td className="p-1">{formatDate(m.berlaku_sejak)}</td></tr>))}</tbody>
          </table>
          <Paginasi daftar={dMaster} testId="payroll-master-paginasi" />
          </div>)}
      </Card>

      <Card className="p-4 space-y-3">
        <div className="font-medium">Periode gaji</div>
        <div className="grid gap-2 sm:grid-cols-4">
          <select className="h-9 rounded-md border px-2 text-sm" data-testid="payroll-periode-region" value={fp.region_id} onChange={(e) => setFp({ ...fp, region_id: e.target.value })}>
            <option value="">— wilayah —</option>{regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <select className="h-9 rounded-md border px-2 text-sm" value={fp.jenis_periode} onChange={(e) => setFp({ ...fp, jenis_periode: e.target.value })}>
            <option value="BULANAN">Bulanan</option><option value="MINGGUAN">Mingguan</option>
          </select>
          <Input type="date" data-testid="payroll-periode-mulai" value={fp.tanggal_mulai} onChange={(e) => setFp({ ...fp, tanggal_mulai: e.target.value })} />
          <Input type="date" data-testid="payroll-periode-selesai" value={fp.tanggal_selesai} onChange={(e) => setFp({ ...fp, tanggal_selesai: e.target.value })} />
        </div>
        <Button variant="outline" disabled={sibuk || !fp.region_id || !fp.tanggal_mulai || !fp.tanggal_selesai}
                onClick={() => jalan(() => api.post("/payroll/periode", fp).then(() => setFp(kosongPeriode)), "Periode dibuat.")} data-testid="payroll-periode-buat">Buat periode</Button>
        <PalangDaftar daftar={dPeriode} placeholder="Cari nama wilayah…" testId="payroll-periode-palang">
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={dPeriode.nilaiSaringan.status}
                  onChange={(e) => dPeriode.setSaringan("status", e.target.value)} aria-label="Saring status" data-testid="payroll-periode-filter-status">
            <option value="">Semua status</option><option value="DRAFT">Draf</option><option value="FINAL">Final</option>
          </select>
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={dPeriode.nilaiSaringan.region}
                  onChange={(e) => dPeriode.setSaringan("region", e.target.value)} aria-label="Saring wilayah" data-testid="payroll-periode-filter-region">
            <option value="">Semua wilayah</option>{regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </PalangDaftar>
        <div className="space-y-1">
          {dPeriode.total === 0 && <div className="text-sm text-muted-foreground">{dPeriode.saringanAktif.length ? "Tidak ada yang cocok." : "Belum ada periode."}</div>}
          {dPeriode.tampil.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 border-t pt-2 text-sm" data-testid={`payroll-periode-${p.id}`}>
              <span>
                {p.region?.name} · {LABEL_JENIS_PERIODE[p.jenis_periode] || p.jenis_periode} · {formatDate(p.tanggal_mulai)} – {formatDate(p.tanggal_selesai)} · <b>{LABEL_STATUS_PERIODE[p.status] || p.status}</b>
                {p.tanggal_bayar && <> · dibayar <b>{formatDate(p.tanggal_bayar)}</b></>}
                {p.keterangan_bayar && <span className="block text-xs text-warning-soft-foreground" data-testid={`payroll-geser-${p.id}`}>{p.keterangan_bayar}</span>}
              </span>
              <span className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => muatSlip(p)} data-testid={`payroll-lihat-${p.id}`}>Lihat slip</Button>
                {p.status === "DRAFT" && <Button size="sm" disabled={sibuk} onClick={() => jalan(() => api.post(`/payroll/periode/${p.id}/hitung`), "Slip dihitung.")} data-testid={`payroll-hitung-${p.id}`}>Hitung</Button>}
                {p.status === "DRAFT" && isOwner && <Button size="sm" variant="destructive" disabled={sibuk} onClick={() => jalan(() => api.post(`/payroll/periode/${p.id}/final`), "Periode difinalkan.")} data-testid={`payroll-final-${p.id}`}>Finalkan</Button>}
              </span>
            </div>
          ))}
          <Paginasi daftar={dPeriode} testId="payroll-periode-paginasi" />
        </div>
      </Card>

      {pilih && (
        <Card className="p-4 space-y-3" data-testid="payroll-slip">
          <div className="font-medium">Slip — {pilih.region?.name} {formatDate(pilih.tanggal_mulai)} – {formatDate(pilih.tanggal_selesai)}</div>
          {slip.length === 0 ? <div className="text-sm text-muted-foreground">Belum ada slip — tekan Hitung (butuh master gaji berjenis sama).</div> : slip.map((s) => {
            const manual = s._manual ?? (s.baris || []).filter((b) => b.kode === "MANUAL");
            return (
              <div key={s.id} className="border rounded p-3 text-sm space-y-1">
                <div className="font-medium">{s.employee?.full_name} — total {formatIDR(s.total_idr)}{s.nomor && <span className="text-xs text-muted-foreground"> · {s.nomor}</span>}</div>
                <div className="text-xs text-muted-foreground">
                  {s.rekap_absensi?.hari_kerja != null && <>Hari kerja {s.rekap_absensi.hari_kerja} · </>}
                  Hadir {s.rekap_absensi?.hadir ?? 0} · izin {s.rekap_absensi?.izin ?? 0} · sakit {s.rekap_absensi?.sakit ?? 0} · cuti {s.rekap_absensi?.cuti ?? 0} · alpa {s.rekap_absensi?.tidak_hadir ?? 0}
                  {s.rekap_absensi?.hari_uang_makan != null && s.rekap_absensi.hari_uang_makan !== s.rekap_absensi.hadir && <> · uang makan {s.rekap_absensi.hari_uang_makan} hari (termasuk izin setengah hari)</>}
                  {s.rekap_absensi?.tanpa_catatan > 0 && <span className="text-warning-soft-foreground"> · {s.rekap_absensi.tanpa_catatan} hari kerja tanpa catatan absen (tidak dipotong — periksa absensinya)</span>}
                </div>
                {(s.baris || []).filter((b) => b.kode !== "MANUAL").map((b, i) => <div key={i} className="flex justify-between"><span>{b.label}</span><span>{formatIDR(b.nominal)}</span></div>)}
                {manual.map((b, i) => (
                  <div key={`m${i}`} className="flex flex-wrap gap-2">
                    <select className="h-9 rounded-md border px-2 text-sm" disabled={pilih.status !== "DRAFT"} value={b.jenis || "LAIN"} onChange={(e) => ubahManual(s, i, { jenis: e.target.value })}>
                      {JENIS_MANUAL.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                    </select>
                    <Input className="flex-1 min-w-[10rem]" disabled={pilih.status !== "DRAFT"} placeholder="Label (kosong = nama jenis)" value={b.label} onChange={(e) => ubahManual(s, i, { label: e.target.value })} />
                    <Input className="w-40" type="number" disabled={pilih.status !== "DRAFT"} value={b.nominal} onChange={(e) => ubahManual(s, i, { nominal: e.target.value })} title="Nominal (Rp)" />
                  </div>
                ))}
                {pilih.status === "DRAFT" && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => tambahManual(s)}>+ Baris manual</Button>
                    <Button size="sm" disabled={sibuk} onClick={() => jalan(() => api.put(`/payroll/slip/${s.id}/manual`, { baris: manual.map((b) => ({ jenis: b.jenis || "LAIN", label: b.label || null, nominal: Number(b.nominal || 0) })) }), "Slip disimpan.")}>Simpan baris manual</Button>
                  </div>
                )}
                {pilih.status === "FINAL" && (
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Button size="sm" variant="outline" onClick={() => bukaPdf(s)} data-testid={`payroll-pdf-${s.id}`}>Slip PDF</Button>
                    {s.dibayar_pada ? (
                      <span className="text-xs text-muted-foreground">Dibayar {formatDate(s.dibayar_pada)}{s.referensi_bayar ? ` · ref ${s.referensi_bayar}` : ""}</span>
                    ) : isFinance ? (
                      <>
                        <Input className="w-48" placeholder="Referensi transfer" value={s._ref || ""}
                               onChange={(e) => setSlip((arr) => arr.map((x) => x.id !== s.id ? x : { ...x, _ref: e.target.value }))} />
                        <Button size="sm" disabled={sibuk} onClick={() => bayar(s)} data-testid={`payroll-bayar-${s.id}`}>Catat dibayar</Button>
                      </>
                    ) : <span className="text-xs text-muted-foreground">Belum dibayar (dicatat Finance)</span>}
                  </div>
                )}
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
